import React from 'react';
import I18n from 'i18n-js';
import BumpRecommendationBody from './bump_modal_body_container';
import { BumpModalShell } from './BumpModalShell';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { formatBid } from './bump_helpers';
import { BumpControllerProps, bumpRecommendationsController, WrappedBumpComponentProps } from './bump_controller';

const COMPONENT_NAME = 'ProductPageBump';

interface ExternalProps {
  showModal?: boolean;
  hidePercentCallout?: boolean;
  buttonText?: string | React.ReactNode | JSX.Element;
  buttonClassName?: string;
  refetch?: (newBid: number | null) => void;
  onModalClose?: () => void;
  disabled?: boolean;
  useFeaturedListingInsightPreview?: boolean;
}

interface PercentCalloutProps {
  bid: number;
}

function PercentCallout(props: PercentCalloutProps) {
  if (!props.bid) {
    return null;
  }

  return (
    <SanitizedRender
      htmlTag="div"
      className="size-90 opacity-70 mb-2"
      html={I18n.t('discovery.bumpButton.bumpAmount',
        {
          tagOpen: '<b>',
          tagClose: '</b>',
          amount: `${formatBid(props.bid)}%`,
        },
      )}
    />
  );
}

export function BumpButton(props: WrappedBumpComponentProps) {
  const buttonText = props.buttonText || I18n.t(`discovery.bumpButton.${props.bid ? 'buttonEdit' : 'buttonBump'}`);
  const buttonClassName = props.buttonClassName || 'button button--primary';

  return (
    <div className="align-center">
      {!props.hidePercentCallout && <PercentCallout bid={props.bid} />}
      <button
        className={buttonClassName}
        onClick={props.openModal}
        disabled={props.disabled}
        type="button"
        aria-label={I18n.t('discovery.bumpButton.buttonAriaLabel')}
      >
        {buttonText}
      </button>
      <BumpModalShell
        isOpen={props.showModal}
        onRequestClose={props.closeModal}
        bid={props.bid}
        showCustomWarning={props.showCustomWarning}
        onSubmit={props.onSubmit}
        onNoThankYou={props.onNoThankYou}
        showTopPickVariant={props.showTopPickVariant}
      >
        <BumpRecommendationBody
          hideSubmit
          hideSuccessMessage
          bid={props.bid}
          selectedBid={props.selectedBid}
          handleBidChange={props.handleBidChange}
          listing={props.listing}
          bumpRecommendationAmount={props.bumpRecommendationAmount}
          listingLoading={props.listingLoading}
          successMessage={props.successMessage}
          failMessage={props.failMessage}
          showCustomWarning={props.showCustomWarning}
          setShowCustomWarning={props.setShowCustomWarning}
          onSubmit={props.onSubmit}
          showTopPickVariant={props.showTopPickVariant}
          startAtMinimumRate={props.startAtMinimumRate}
        />
      </BumpModalShell>
    </div>
  );
}

const BumpButtonContainer = bumpRecommendationsController(BumpButton);

export default function BumpButtonWrapper(props: ExternalProps & BumpControllerProps) {
  return (
    <BumpButtonContainer
      componentName={COMPONENT_NAME}
      listingId={props.listingId}
      bid={props.bid}
      showModal={props.showModal}
      hidePercentCallout={props.hidePercentCallout}
      buttonText={props.buttonText}
      buttonClassName={props.buttonClassName}
      refetch={props.refetch}
      onModalClose={props.onModalClose}
      disabled={props.disabled}
      useFeaturedListingInsightPreview={props.useFeaturedListingInsightPreview}
      startAtMinimumRate={props.startAtMinimumRate}
    />
  );
}
